<template>
  <div
    class="game-container"
    :class="{ 'high-contrast-mode': highContrastMode }"
  >
    <!-- Animation de badge débloqué -->
    <div v-if="showBadgeUnlockAnimation" class="badge-unlock-overlay">
      <div class="badge-unlock-animation">
        <img :src="badgeScenarioImage" alt="Badge" class="badge-icon" />
        <h2>Badge débloqué !</h2>
        <h3>Maître des scénarios</h3>
        <p>Vous avez brillamment résolu tous les scénarios sociaux !</p>
        <button @click="closeBadgeAnimation" class="close-animation-btn">
          Continuer
        </button>
      </div>
    </div>

    <div class="game-header">
      <!-- Barre de progression -->
      <div class="progress-bar">
        <div class="progress-step" :class="{ completed: phase !== 'intro' }">
          <div class="step-icon">📖</div>
          <div class="step-label">Introduction</div>
        </div>
        <div class="progress-connector"></div>
        <div
          class="progress-step"
          :class="{ completed: phase === 'choix' || feedback }"
        >
          <div class="step-icon">💬</div>
          <div class="step-label">Dialogue</div>
        </div>
        <div class="progress-connector"></div>
        <div class="progress-step" :class="{ completed: feedback }">
          <div class="step-icon">🤔</div>
          <div class="step-label">Décision</div>
        </div>
      </div>

      <!-- Titre du scénario -->
      <h1 v-if="scenario" class="scenario-title">
        <span class="title-decoration">★</span>
        {{ scenario.titre }}
        <span class="title-decoration">★</span>
      </h1>
    </div>

    <div v-if="scenario" class="game-content">
      <!-- Panneau d'introduction -->
      <div v-if="phase === 'intro'" class="intro-panel">
        <div class="story-card">
          <div class="story-header">La situation</div>
          <p class="story-text">{{ scenario.contexteIntro }}</p>
          <button @click="startDialogue" class="action-button start-button">
            <span class="button-icon">▶️</span>
            <span class="button-text">Commencer</span>
          </button>
        </div>

        <!-- Illustration -->
        <div class="scene-illustration">
          <img
            :src="getSceneImage()"
            alt="Illustration de la scène"
            class="scene-image"
          />
        </div>
      </div>

      <!-- Zone de dialogue -->
      <div v-else class="dialogue-zone">
        <!-- Bulle de narration pour guider -->
        <div
          v-if="dialogueIndex === 0 && phase === 'dialogue'"
          class="narrator-bubble"
        >
          <p>Observe la conversation et décide comment tu réagirais...</p>
        </div>

        <!-- Bulles de dialogue -->
        <transition-group
          name="dialogue-appear"
          tag="div"
          class="dialogue-container"
        >
          <div
            v-for="(dialogue, index) in visibleDialogues"
            :key="index"
            class="dialogue"
            :class="getDialogueClass(dialogue.personnage)"
          >
            <div class="character-avatar">
              <img
                :src="getAvatarPath(dialogue.avatar)"
                :alt="dialogue.personnage"
              />
              <div class="character-name">{{ dialogue.personnage }}</div>
            </div>
            <div class="speech-bubble">
              <p>{{ dialogue.texte }}</p>
            </div>
          </div>
        </transition-group>

        <!-- Bouton pour avancer dans le dialogue -->
        <button
          v-if="
            dialogueIndex < scenario.contexte.length - 1 && phase === 'dialogue'
          "
          @click="showNextDialogue"
          class="action-button next-button"
        >
          <span class="button-text">Suivant</span>
          <span class="button-icon">👉</span>
        </button>

        <!-- Question et choix -->
        <div v-if="phase === 'choix'" class="choice-panel">
          <div class="question-box">
            <h2 class="question-text">{{ scenario.question }}</h2>
          </div>

          <div class="choices-container">
            <button
              v-for="(reponse, index) in scenario.reponses"
              :key="index"
              @click="choisirReponse(reponse)"
              class="choice-button"
              :class="{ selected: selectedChoice === index }"
              :style="{ '--delay': index * 0.2 + 's' }"
            >
              {{ reponse.texte }}
            </button>
          </div>
        </div>

        <!-- Feedback après la sélection -->
        <div v-if="feedback" class="feedback-panel">
          <!-- Flamou Congratulations Section -->
          <div v-if="flamouCongratulations" class="flamou-congratulations">
            <div class="flamou-character">
              <img
                :src="flamouCongratulations.image"
                alt="Flamou heureux"
                class="flamou-avatar"
              />
              <div class="flamou-speech-bubble">
                <p class="congratulation-message">{{ flamouCongratulations.message }}</p>
              </div>
            </div>
          </div>

          <!-- Original Feedback Content -->
          <div class="feedback-content">
            <div class="feedback-icon">✅</div>
            <p class="feedback-message">{{ feedback }}</p>
            <div class="feedback-animation"></div>
          </div>

          <!-- Bouton pour passer à la question suivante -->
          <div class="next-scenario-container">
            <button @click="goToNextScenario" class="action-button next-scenario-button">
              <span class="button-icon">➡️</span>
              <span class="button-text">{{ isLastScenario ? 'Terminer' : 'Question suivante' }}</span>
            </button>
          </div>
        </div>
      </div>
    </div>

    <div v-if="!feedback" class="return-menu-container">
      <button @click="retournerAuMenu" class="action-button return-button">
        <span class="button-icon">🏠</span>
        <span class="button-text">Retour au menu</span>
      </button>
    </div>

    <!-- Contrôles d'accessibilité -->
    <div class="accessibility-controls">
      <button @click="toggleSound" class="accessibility-button">
        <span v-if="soundEnabled">🔊</span>
        <span v-else>🔇</span>
      </button>
      <button @click="toggleHighContrast" class="accessibility-button">
        <span v-if="highContrastMode">🌓</span>
        <span v-else>🌑</span>
      </button>
    </div>

    <!-- Message de chargement -->
    <div v-if="!scenario" class="loading-container">
      <div class="loading-animation"></div>
      <p>Chargement de l'aventure...</p>
    </div>
  </div>
</template>

<script>
import { scenarios } from '@/data/data.js'
import { unlockBadge } from '@/utils/badges.js'
const avatars =
  typeof require !== 'undefined' && typeof require.context === 'function'
    ? require.context('@/assets/avatars/', false, /\.png$/)
    : () => require('@/assets/avatars/toi.png')
import AuthService from '@/services/AuthService'
import flamouImage from '@/assets/flamou/intresting.png'
import flamouHappy from '@/assets/flamou/happy.png'
import flamouHappy2 from '@/assets/flamou/happy2.png'
import flamouThinking from '@/assets/flamou/thinking1.png'
import badgeScenarioImage from '@/assets/badges_skills/Badge_jeu_de_scénarios.png'

export default {
  name: 'ScenarioPage',
  props: {
    urlName: String,
  },
  setup() {
    return {
      flamouImage,
      flamouHappy,
      flamouHappy2,
      flamouThinking,
      badgeScenarioImage,
    }
  },
  data() {
    return {
      scenario: null,
      scenarios: scenarios,
      feedback: null,
      phase: 'intro',
      dialogueIndex: 0,
      selectedChoice: null,
      lastChoiceSkills: [],
      soundEnabled: true,
      highContrastMode: false,
      textSizeLevel: 0,
      completedScenarios: [],
      flamouCongratulations: null,
      congratulationMessages: [
        "Bravo ! 🌟",
        "Super ! 🎉",
        "Excellent ! ✨",
        "Fantastique ! 🏆",
        "Bien joué ! 🌱",
        "Magnifique ! 💫",
        "Formidable ! 👏",
        "Incroyable ! 🌈"
      ],
      isLastScenario: false,
      showBadgeUnlockAnimation: false,
    }
  },
  computed: {
    visibleDialogues() {
      return this.scenario
        ? this.scenario.contexte.slice(0, this.dialogueIndex + 1)
        : []
    },
  },
  created() {
    this.loadProgress()
    this.loadScenario()
    this.loadAccessibilitySettings()
  },
  mounted() {
    // Ajouter un écouteur d'événement pour la navigation (quand on quitte le composant)
    window.addEventListener('beforeunload', this.stopSpeech)
  },
  beforeUnmount() {
    // S'assurer que la synthèse vocale est arrêtée quand le composant est détruit
    this.stopSpeech()
    // Supprimer l'écouteur d'événement
    window.removeEventListener('beforeunload', this.stopSpeech)
  },
  watch: {
    urlName(newUrlName, oldUrlName) {
      if (newUrlName !== oldUrlName) {
        this.stopSpeech()
        this.loadScenario()
        this.phase = 'intro'
        this.feedback = null
        this.dialogueIndex = 0
        this.selectedChoice = null
        this.lastChoiceSkills = []
      }
    },
    highContrastMode() {
      this.saveAccessibilitySettings()
    },
    textSizeLevel() {
      this.saveAccessibilitySettings()
      this.applyTextSize()
    },
    soundEnabled() {
      this.saveAccessibilitySettings()
      if (!this.soundEnabled) {
        this.stopSpeech()
      }
    },
  },
  methods: {
    loadProgress() {
      const completedIds = localStorage.getItem('completedScenarios')
      if (completedIds) {
        this.completedScenarios = JSON.parse(completedIds).map((id) =>
          parseInt(id),
        )
      }
    },

    retournerAuMenu() {
      this.playSound('button')

      this.stopSpeech()

      this.$router.push({ name: 'Dashboard' })
    },

    loadAccessibilitySettings() {
      const settings = localStorage.getItem('accessibilitySettings')
      if (settings) {
        const { soundEnabled, highContrastMode, textSizeLevel } =
          JSON.parse(settings)
        this.soundEnabled = soundEnabled
        this.highContrastMode = highContrastMode
        this.textSizeLevel = textSizeLevel
        this.applyTextSize()
      }
    },

    saveAccessibilitySettings() {
      const settings = {
        soundEnabled: this.soundEnabled,
        highContrastMode: this.highContrastMode,
        textSizeLevel: this.textSizeLevel,
      }
      localStorage.setItem('accessibilitySettings', JSON.stringify(settings))
    },

    applyTextSize() {
      const container = document.querySelector('.game-container')
      if (!container) return

      // Supprimer les classes de taille précédentes
      container.classList.remove('text-small', 'text-large')

      // Ajouter la classe appropriée
      if (this.textSizeLevel === -1) {
        container.classList.add('text-small')
      } else if (this.textSizeLevel === 1) {
        container.classList.add('text-large')
      }
    },

    toggleSound() {
      this.soundEnabled = !this.soundEnabled
    },

    toggleHighContrast() {
      this.highContrastMode = !this.highContrastMode
    },

    increaseTextSize() {
      if (this.textSizeLevel < 1) {
        this.textSizeLevel++
      }
    },

    decreaseTextSize() {
      if (this.textSizeLevel > -1) {
        this.textSizeLevel--
      }
    },

    loadScenario() {
      this.scenario = scenarios.find((s) => s.urlName === this.urlName)

      if (!this.scenario) {
        console.error('Scénario non trouvé !')
      } else {
        if (this.soundEnabled) {
          this.readAloud(this.scenario.contexteIntro)
        }
        console.log('Scénario chargé:', this.scenario)
        console.log('Réponses disponibles:', this.scenario.reponses)

        this.animateText()
      }
    },

    animateText() {
      // Animation de texte qui apparaît progressivement (pourrait être implémentée avec JavaScript)
      // Cette fonction est un placeholder pour une future animation
    },

    startDialogue() {
      this.phase = 'dialogue'
      this.dialogueIndex = 0

      this.playSound('button')

      if (this.scenario.contexte.length > 0 && this.soundEnabled) {
        this.readAloud(this.scenario.contexte[0].texte)
      }
    },

    showNextDialogue() {
      console.log('showNextDialogue appelée')
      console.log('dialogueIndex:', this.dialogueIndex)
      console.log('Longueur contexte:', this.scenario.contexte.length)
      this.playSound('next')

      if (this.dialogueIndex < this.scenario.contexte.length - 1) {
        this.dialogueIndex++

        if (this.soundEnabled) {
          this.readAloud(this.scenario.contexte[this.dialogueIndex].texte)
        }
      } else {
        this.phase = 'choix'

        if (this.soundEnabled) {
          this.readAloud(this.scenario.question)
        }
      }
      if (this.dialogueIndex >= this.scenario.contexte.length - 1) {
        console.log('Passage à la phase choix')
        this.phase = 'choix'

        console.log('Phase actuelle:', this.phase)
        console.log('Réponses disponibles:', this.scenario.reponses)
      }
    },

    async choisirReponse(reponse) {
      this.selectedChoice = this.scenario.reponses.indexOf(reponse)

      // Generate Flamou congratulations
      const randomCongratulation = this.congratulationMessages[
        Math.floor(Math.random() * this.congratulationMessages.length)
      ]

      // Choose appropriate Flamou image (random between happy expressions)
      const flamouImages = [this.flamouHappy, this.flamouHappy2]
      const randomFlamouImage = flamouImages[Math.floor(Math.random() * flamouImages.length)]

      this.flamouCongratulations = {
        message: randomCongratulation,
        image: randomFlamouImage
      }

      this.feedback = `Tu as choisi : "${reponse.texte}"`

      this.playSound('choice')

      if (this.soundEnabled) {
        // Read Flamou's congratulation first, then the feedback
        this.readAloud(`${randomCongratulation}. ${this.feedback}`)
      }

      this.lastChoiceSkills = this.extractSkills(reponse)

      this.enregistrerSoftSkills(reponse)

      this.markScenarioCompleted()

      const currentId = this.scenario.id
      this.isLastScenario = !this.scenarios.some((s) => s.id === currentId + 1)

      if (this.isLastScenario) {
        // Déverrouiller le badge "Maître des scénarios"
        await unlockBadge(2)

        // Stocker l'information que nous venons de déverrouiller un badge
        localStorage.setItem(
          'newUnlockedBadge',
          JSON.stringify({
            id: 2,
            title: 'Maître des scénarios',
            description:
              'Vous avez brillamment résolu tous les scénarios sociaux !',
          }),
        )
      }
    },

    goToNextScenario() {
      this.stopSpeech()

      if (this.isLastScenario) {
        // Afficher l'animation du badge débloqué
        this.showBadgeUnlockAnimation = true
      } else {
        const currentId = this.scenario.id
        const nextId = currentId + 1
        const nextScenario = scenarios.find((s) => s.id === nextId)
        if (nextScenario) {
          this.$router.push({
            name: 'ScenarioPage',
            params: { urlName: nextScenario.urlName },
          })
        }
      }
    },

    closeBadgeAnimation() {
      this.showBadgeUnlockAnimation = false
      // Rediriger vers le Dashboard après avoir fermé l'animation
      this.$router.push({ name: 'Dashboard', query: { showBadges: 'true' } })
    },

    markScenarioCompleted() {
      const currentId = this.scenario.id

      if (!this.completedScenarios.includes(currentId)) {
        this.completedScenarios.push(currentId)
        localStorage.setItem(
          'completedScenarios',
          JSON.stringify(this.completedScenarios),
        )
      }
    },

    extractSkills(reponse) {
      const skills = []

      // Extraire les compétences positives
      if (reponse.skills) {
        Object.entries(reponse.skills).forEach(([name, value]) => {
          skills.push({ name, value, type: 'positive' })
        })
      }

      // Extraire les pénalités (compétences négatives)
      if (reponse.penalties) {
        Object.entries(reponse.penalties).forEach(([name, value]) => {
          skills.push({ name, value: -value, type: 'negative' })
        })
      }

      // Trier par valeur (du plus grand au plus petit)
      return skills.sort((a, b) => Math.abs(b.value) - Math.abs(a.value))
    },

    readAloud(text) {
      if ('speechSynthesis' in window && this.soundEnabled) {
        // Arrêter toute synthèse vocale en cours
        this.stopSpeech()

        const utterance = new SpeechSynthesisUtterance(text)
        utterance.lang = 'fr-FR'
        utterance.rate = 1
        utterance.pitch = 1
        window.speechSynthesis.speak(utterance)
      }
    },

    stopSpeech() {
      if ('speechSynthesis' in window) {
        window.speechSynthesis.cancel()
      }
    },

    playSound(type) {
      // Cette fonction pourrait jouer différents sons selon le type d'action
      // Par exemple: 'button', 'next', 'choice', 'success'
      if (!this.soundEnabled) return

      // Implémentation de base avec l'API Audio
      // Pour une implémentation complète, il faudrait ajouter les fichiers audio
      const sounds = {
        button: 'button_click.mp3',
        next: 'next_dialog.mp3',
        choice: 'choice_made.mp3',
        success: 'success.mp3',
      }

      console.log(
        `Le son "${sounds[type]}" serait joué pour l'action "${type}"`,
      )
      // Si vous avez les fichiers audio, décommentez ce code
      /*
        try {
          const sound = new Audio(require(`@/assets/sounds/${sounds[type]}`));
          sound.play();
        } catch (e) {
          console.log('Son non disponible:', e);
        }
        */
    },

    enregistrerSoftSkills(reponse) {
      // Récupérer les compétences déjà enregistrées ou initialiser un objet vide
      const savedSkills =
        JSON.parse(localStorage.getItem('userSoftSkills')) || {}

      // Traiter les compétences positives
      if (reponse.skills) {
        for (const [skill, points] of Object.entries(reponse.skills)) {
          savedSkills[skill] = (savedSkills[skill] || 0) + points
        }
      }

      // Traiter les pénalités (compétences négatives)
      if (reponse.penalties) {
        for (const [skill, points] of Object.entries(reponse.penalties)) {
          savedSkills[skill] = (savedSkills[skill] || 0) - points
          // Éviter les scores négatifs
          if (savedSkills[skill] < 0) savedSkills[skill] = 0
        }
      }

      // Sauvegarder les compétences mises à jour dans localStorage
      localStorage.setItem('userSoftSkills', JSON.stringify(savedSkills))

      // Sauvegarder vers le backend
      this.saveProgressToBackend(savedSkills)
    },

    saveProgressLocally(skills) {
      // Calculer le niveau actuel et la complétion
      const completedScenarios = JSON.parse(
        localStorage.getItem('completedScenarios') || '[]',
      )

      // S'assurer que nous avons des nombres valides
      const current_level =
        completedScenarios.length > 0 ? Math.max(...completedScenarios) : 0

      // Calculer la complétion (entre 0 et 1)
      const totalScenarios = scenarios.length
      const completionDecimal =
        totalScenarios > 0 ? current_level / totalScenarios : 0

      // S'assurer que la complétion est un nombre valide entre 0 et 1
      const completion = isNaN(completionDecimal)
        ? 0
        : parseFloat(completionDecimal.toFixed(4))

      // Utiliser directement les compétences individuelles
      const traits = { ...skills }

      // Créer l'objet à sauvegarder
      const progressData = {
        current_level,
        completion,
        traits,
      }

      console.log(
        `Complétion locale Scenario: ${(completion * 100).toFixed(1)}% (${current_level}/${totalScenarios} scénarios)`,
      )

      // Sauvegarder dans localStorage
      localStorage.setItem(
        'scenarioProgressBackup',
        JSON.stringify(progressData),
      )
      console.log('Sauvegarde locale de la progression:', progressData)
    },

    saveProgressToBackend(skills) {
      // D'abord récupérer les données déjà sauvegardées
      AuthService.request('get', '/games/scenario')
        .then((response) => {
          // Initialiser les données si elles n'existent pas
          let data =
            response.data && typeof response.data === 'object'
              ? response.data
              : { current_level: 0, completion: 0, traits: {} }

          // S'assurer que currentLevel est un nombre valide
          if (
            typeof data.current_level !== 'number' ||
            isNaN(data.current_level)
          ) {
            data.current_level = 0
          }

          // Mettre à jour le niveau actuel
          const currentScenarioId = this.scenario.id
          if (currentScenarioId > data.current_level) {
            data.current_level = currentScenarioId
          }

          // Calculer le pourcentage de complétion (entre 0 et 1)
          const totalScenarios = scenarios.length
          const completionDecimal =
            totalScenarios > 0 ? data.current_level / totalScenarios : 0

          // S'assurer que la complétion est un nombre entre 0 et 1 arrondi au centième
          data.completion = isNaN(completionDecimal)
            ? 0
            : parseFloat(completionDecimal.toFixed(4))

          // Utiliser directement les compétences individuelles
          data.traits = { ...skills }

          console.log(
            `Complétion Scenario: ${(data.completion * 100).toFixed(1)}% (${data.current_level}/${totalScenarios} scénarios)`,
          )
          console.log('Données à envoyer au backend:', data)

          // Envoyer les données au backend
          return AuthService.request('post', '/games/scenario', data)
        })
        .then((response) => {
          console.log('Progression sauvegardée avec succès:', response.data)
        })
        .catch((error) => {
          console.error(
            'Erreur lors de la sauvegarde de la progression:',
            error,
          )
          // Sauvegarde locale comme fallback
          this.saveProgressLocally(skills)
        })
    },

    getAvatarPath(filename) {
      if (!filename) return require('@/assets/avatars/toi.png')

      const path = `./${filename}`
      try {
        return avatars(path)
      } catch {
        return require('@/assets/avatars/toi.png')
      }
    },

    getDialogueClass(personnage) {
      return personnage === 'Toi' ? 'dialogue-right' : 'dialogue-left'
    },

    getSceneImage() {
      // Idéalement, vous auriez des images spécifiques pour chaque scénario
      // Voici quelques exemples de correspondance scénario-image
      const sceneImages = {
        1: require('@/assets/avatars/personne_agee.png'), // exemple
        2: require('@/assets/avatars/femme-caissiere.png'), // exemple
        // Ajoutez d'autres correspondances selon vos scénarios
      }

      const scenarioId = parseInt(this.id)
      return sceneImages[scenarioId] || require('@/assets/avatars/toi.png')
    },

    getSkillIcon(skillName) {
      // Association d'emojis aux compétences
      const skillIcons = {
        assertivite: '🗣️',
        communication: '💬',
        empathie: '❤️',
        espritEquipe: '👥',
        initiative: '🚀',
        adaptabilite: '🔄',
        responsabilite: '🛡️',
        creativite: '💡',
        observation: '👁️',
        diplomatie: '🤝',
        patience: '⏳',
        confianceEnSoi: '💪',
        inclusion: '🌈',
        mediation: '☮️',
        coaching: '📚',
        ecouteActive: '👂',
        soutien: '🤲',
        reflexionRapide: '⚡',
        negociation: '🔍',
        gestionStress: '😌',
        anticipation: '🔮',
        apprentissage: '🧠',
      }

      return skillIcons[skillName] || '✨'
    },

    getSkillDisplayName(skillName) {
      // Noms des compétences formatés pour l'affichage
      const skillNames = {
        assertivite: 'Assertivité',
        communication: 'Communication',
        empathie: 'Empathie',
        espritEquipe: "Esprit d'équipe",
        initiative: 'Initiative',
        adaptabilite: 'Adaptabilité',
        responsabilite: 'Responsabilité',
        creativite: 'Créativité',
        observation: 'Observation',
        diplomatie: 'Diplomatie',
        patience: 'Patience',
        confianceEnSoi: 'Confiance en soi',
        inclusion: 'Inclusion',
        mediation: 'Médiation',
        coaching: 'Coaching',
        ecouteActive: 'Écoute active',
        soutien: 'Soutien',
        reflexionRapide: 'Réflexion rapide',
        negociation: 'Négociation',
        gestionStress: 'Gestion du stress',
        anticipation: 'Anticipation',
        apprentissage: 'Apprentissage',
      }

      return skillNames[skillName] || skillName
    },
  },
}
</script>

<style scoped>
/* Container principal */
.game-container {
  font-family: 'Comic Sans MS', 'Chalkboard SE', 'Marker Felt', sans-serif;
  max-width: 900px;
  margin: 20px auto;
  padding: 20px;
  background-color: #f8f9fa;
  border-radius: 20px;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.1);
  position: relative;
  overflow: hidden;
}

/* Adaptation mode contraste élevé */
.game-container.high-contrast-mode {
  background-color: #000;
  color: #fff;
}

/* Adaptations de taille de texte */
.game-container.text-small {
  font-size: 0.9em;
}

.game-container.text-large {
  font-size: 1.2em;
}

/* En-tête du jeu */
.game-header {
  margin-bottom: 20px;
  padding-top: 40px; /* Ajouter un espace en haut du header pour les contrôles d'accessibilité */
  position: relative;
}

/* Barre de progression */
.progress-bar {
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 15px;
  margin-top: 10px;
  position: relative;
  z-index: 50; /* Z-index inférieur aux contrôles d'accessibilité */
}

.progress-step {
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;
  width: 80px;
}

.step-icon {
  width: 40px;
  height: 40px;
  background-color: #e9ecef;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.2rem;
  margin-bottom: 5px;
  border: 2px solid #ced4da;
  position: relative;
  z-index: 2;
}

.step-label {
  font-size: 0.8rem;
  color: #6c757d;
}

.return-menu-container {
  display: flex;
  justify-content: center;
  margin-top: 20px;
  margin-bottom: 20px;
}

/* Style pour le bouton de retour */
.return-button {
  background-color: #6c757d;
}

.return-button:hover {
  background-color: #5a6268;
}
.progress-connector {
  height: 2px;
  background-color: #ced4da;
  flex-grow: 1;
  margin: 0 -10px;
  position: relative;
  top: -20px;
  z-index: 1;
}

.progress-step.completed .step-icon {
  background-color: #4caf50;
  color: white;
  border-color: #4caf50;
}

.progress-step.completed .step-label {
  color: #4caf50;
  font-weight: bold;
}

/* Titre du scénario */
.scenario-title {
  text-align: center;
  font-size: 2rem;
  color: #2c3e50;
  margin: 15px 0;
  text-shadow: 1px 1px 3px rgba(0, 0, 0, 0.1);
}

.title-decoration {
  color: #ffd700;
  margin: 0 10px;
}

/* Contenu du jeu */
.game-content {
  background-color: white;
  border-radius: 15px;
  overflow: hidden;
  box-shadow: 0 3px 10px rgba(0, 0, 0, 0.1);
  position: relative;
}

.intro-panel {
  display: flex;
  flex-direction: column;
  padding: 20px;
}

/* Ajoutez cette règle ou modifiez-la si elle existe déjà */
.story-card {
  display: flex;
  flex-direction: column;
  background-color: #f8f9fa;
  border-radius: 12px;
  padding: 20px;
  margin-bottom: 20px;
  box-shadow: 0 3px 8px rgba(0, 0, 0, 0.05);
  position: relative;
  overflow: hidden;
}

.story-header {
  font-size: 1.3rem;
  font-weight: bold;
  color: #495057;
  margin-bottom: 15px;
  text-align: center;
}

.story-text {
  font-size: 1.1rem;
  line-height: 1.6;
  color: #343a40;
  margin-bottom: 20px;
  position: relative;
  z-index: 2;
}

.scene-illustration {
  display: flex;
  justify-content: center;
  margin-top: 10px;
}

.scene-image {
  max-width: 100%;
  max-height: 250px;
  border-radius: 10px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

/* Zone de dialogue */
.dialogue-zone {
  padding: 20px;
  position: relative;
}

.narrator-bubble {
  background-color: #f8f9fa;
  border-radius: 10px;
  padding: 10px 15px;
  margin-bottom: 15px;
  text-align: center;
  border-left: 4px solid #6c757d;
  font-style: italic;
  color: #495057;
  animation: fadeIn 0.5s ease-out;
}

.dialogue-container {
  min-height: 250px;
  position: relative;
}

.dialogue {
  display: flex;
  margin-bottom: 20px;
  align-items: flex-start;
  animation: slideIn 0.5s ease-out;
}

.dialogue-left {
  align-self: flex-start;
}

.dialogue-right {
  flex-direction: row-reverse;
  align-self: flex-end;
}

.character-avatar {
  display: flex;
  flex-direction: column;
  align-items: center;
  margin: 0 10px;
}

.character-avatar img {
  width: 60px;
  height: 60px;
  border-radius: 50%;
  object-fit: cover;
  border: 3px solid #f8f9fa;
  box-shadow: 0 3px 6px rgba(0, 0, 0, 0.1);
}

.character-name {
  font-size: 0.8rem;
  margin-top: 5px;
  text-align: center;
  color: #6c757d;
  max-width: 80px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.speech-bubble {
  position: relative;
  background-color: #e9f5ff;
  border-radius: 15px;
  padding: 12px 15px;
  max-width: 70%;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.05);
}

.dialogue-right .speech-bubble {
  background-color: #e6f7e6;
}

.speech-bubble:before {
  content: '';
  position: absolute;
  top: 20px;
  width: 0;
  height: 0;
  border: 10px solid transparent;
}

.dialogue-left .speech-bubble:before {
  left: -20px;
  border-right-color: #e9f5ff;
}

.dialogue-right .speech-bubble:before {
  right: -20px;
  border-left-color: #e6f7e6;
}

.speech-bubble p {
  margin: 0;
  font-size: 1rem;
  line-height: 1.5;
  color: #343a40;
}

/* Animations de dialogue */
.dialogue-appear-enter-active {
  transition: all 0.5s ease;
}

.dialogue-appear-enter-from {
  opacity: 0;
  transform: translateY(20px);
}

/* Bouton d'action */
.action-button {
  display: inline-flex; /* Changez flex à inline-flex */
  align-items: center;
  justify-content: center;
  background-color: #007bff;
  color: white;
  border: none;
  border-radius: 50px;
  padding: 10px 20px;
  font-size: 1.1rem;
  font-weight: bold;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 3px 6px rgba(0, 0, 0, 0.1);
  margin: 15px auto;
  width: auto;
  height: auto;
}

.action-button:hover {
  transform: translateY(-2px);
  box-shadow: 0 5px 10px rgba(0, 0, 0, 0.2);
}

.action-button:active {
  transform: translateY(1px);
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.15);
}

.button-icon {
  margin: 0 8px;
}

.start-button {
  background-color: #28a745;
}

.next-button {
  background-color: #17a2b8;
}

.next-scenario-button {
  background-color: #2196f3;
}

.next-scenario-button:hover {
  background-color: #1976d2;
}

.next-scenario-container {
  display: flex;
  justify-content: center;
  margin-top: 20px;
}

/* Panneau de choix */
.choice-panel {
  margin-top: 20px;
}

.question-box {
  background-color: #f8f9fa;
  border-left: 5px solid #6c757d;
  padding: 15px;
  margin-bottom: 20px;
  border-radius: 8px;
}

.question-text {
  font-size: 1.2rem;
  font-weight: bold;
  color: #343a40;
  margin: 0;
}

.choices-container {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.choice-button {
  background-color: #f8f9fa;
  border: 2px solid #dee2e6;
  border-radius: 12px;
  padding: 15px;
  text-align: left;
  font-size: 1rem;
  color: #495057;
  cursor: pointer;
  transition: all 0.3s ease;
  animation: fadeIn 0.5s ease-out forwards;
  animation-delay: var(--delay);
  opacity: 0;
  position: relative;
  overflow: hidden;
}

.choice-button:hover {
  border-color: #007bff;
  background-color: #f0f7ff;
  transform: translateX(5px);
}

.choice-button:active {
  transform: translateX(2px);
}

.choice-button.selected {
  border-color: #28a745;
  background-color: #e6f7e6;
  pointer-events: none;
}

.choice-button.selected:after {
  content: '✓';
  position: absolute;
  right: 15px;
  top: 50%;
  transform: translateY(-50%);
  color: #28a745;
  font-weight: bold;
}

/* Flamou Congratulations */
.flamou-congratulations {
  margin-bottom: 20px;
  animation: slideInFromLeft 0.8s ease-out;
}

.flamou-character {
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 15px;
}

.flamou-avatar {
  width: 80px;
  height: 80px;
  border-radius: 50%;
  border: 4px solid #ffd700;
  background-color: #fff;
  box-shadow: 0 6px 12px rgba(255, 215, 0, 0.3);
  animation: bounce 1s ease-out;
  margin-right: 15px;
}

.flamou-speech-bubble {
  position: relative;
  background: linear-gradient(135deg, #ffd700, #ffed4e);
  border-radius: 20px;
  padding: 15px 20px;
  max-width: 300px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  animation: popIn 0.6s ease-out 0.3s both;
}

.flamou-speech-bubble:before {
  content: '';
  position: absolute;
  left: -15px;
  top: 50%;
  transform: translateY(-50%);
  border-width: 15px 15px 15px 0;
  border-style: solid;
  border-color: transparent #ffd700 transparent transparent;
}

.congratulation-message {
  margin: 0;
  font-size: 1.2rem;
  font-weight: bold;
  color: #2c5282;
  text-align: center;
  text-shadow: 1px 1px 2px rgba(255, 255, 255, 0.8);
}

@keyframes slideInFromLeft {
  from {
    opacity: 0;
    transform: translateX(-50px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
}

@keyframes bounce {
  0%, 20%, 50%, 80%, 100% {
    transform: translateY(0);
  }
  40% {
    transform: translateY(-10px);
  }
  60% {
    transform: translateY(-5px);
  }
}

@keyframes popIn {
  from {
    opacity: 0;
    transform: scale(0.8);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
}

/* Panneau de feedback */
.feedback-panel {
  margin-top: 20px;
  animation: fadeIn 0.5s ease-out;
}

.feedback-content {
  background-color: #e6f7e6;
  border-radius: 12px;
  padding: 15px;
  display: flex;
  align-items: center;
  margin-bottom: 20px;
  position: relative;
}

.feedback-icon {
  font-size: 2rem;
  margin-right: 15px;
  color: #28a745;
}

.feedback-message {
  font-size: 1.1rem;
  color: #343a40;
  flex-grow: 1;
  margin: 0;
}

.feedback-animation {
  position: absolute;
  width: 100%;
  height: 100%;
  top: 0;
  left: 0;
  pointer-events: none;
  z-index: 10;
}

/* Compétences gagnées */
.skills-gained {
  background-color: #f8f9fa;
  border-radius: 12px;
  padding: 15px;
  margin-top: 15px;
}

.skills-gained h3 {
  text-align: center;
  color: #495057;
  margin-top: 0;
  margin-bottom: 15px;
  font-size: 1.2rem;
}

.skills-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.skill-item {
  display: flex;
  align-items: center;
  background-color: white;
  border-radius: 8px;
  padding: 10px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  animation: slideUp 0.5s ease-out forwards;
  animation-delay: calc(var(--index) * 0.2s);
  opacity: 0;
  transform: translateY(20px);
}

.skill-icon {
  font-size: 1.5rem;
  margin-right: 12px;
  width: 40px;
  height: 40px;
  background-color: #f0f7ff;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.skill-info {
  flex-grow: 1;
}

.skill-name {
  font-weight: bold;
  color: #343a40;
  margin-bottom: 2px;
}

.skill-points {
  font-size: 0.9rem;
  color: #28a745;
}

.skill-points.negative {
  color: #dc3545;
}

.no-skills {
  text-align: center;
  color: #6c757d;
  font-style: italic;
}

/* Contrôles d'accessibilité */
.accessibility-controls {
  position: absolute;
  top: 0px;
  right: 10px;
  display: flex;
  gap: 5px;
  z-index: 100;
}

.accessibility-button {
  width: 35px;
  height: 35px;
  border-radius: 50%;
  border: none;
  background-color: #f8f9fa;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.9rem;
  cursor: pointer;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.1);
  transition: all 0.2s ease;
}

.accessibility-button:hover {
  background-color: #e9ecef;
  transform: scale(1.1);
}

/* Animation de chargement */
.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 300px;
}

.loading-animation {
  width: 50px;
  height: 50px;
  border: 5px solid #f3f3f3;
  border-top: 5px solid #3498db;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 15px;
}

/* Animations */
@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

@keyframes slideIn {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes slideUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

/* Styles pour le mode contraste élevé */
.high-contrast-mode .game-content {
  background-color: #222;
  border: 1px solid #fff;
}

.high-contrast-mode .story-card,
.high-contrast-mode .narrator-bubble,
.high-contrast-mode .question-box,
.high-contrast-mode .skills-gained {
  background-color: #333;
  color: #fff;
}

.high-contrast-mode .speech-bubble {
  background-color: #444;
  color: #fff;
}

.high-contrast-mode .speech-bubble:before {
  border-right-color: #444;
  border-left-color: #444;
}

.high-contrast-mode .choice-button {
  background-color: #333;
  border-color: #666;
  color: #fff;
}

.high-contrast-mode .choice-button:hover {
  border-color: #fff;
  background-color: #444;
}

.high-contrast-mode .choice-button.selected {
  border-color: #fff;
  background-color: #444;
}

/* High contrast mode for Flamou congratulations */
.high-contrast-mode .flamou-speech-bubble {
  background: #000;
  border: 2px solid #fff;
  color: #fff;
}

.high-contrast-mode .flamou-speech-bubble:before {
  border-color: transparent #fff transparent transparent;
}

.high-contrast-mode .congratulation-message {
  color: #fff;
  text-shadow: none;
}

.high-contrast-mode .flamou-avatar {
  border-color: #fff;
}

/* Responsive design */
@media (max-width: 768px) {
  .game-container {
    margin: 10px;
    padding: 15px;
  }

  .scenario-title {
    font-size: 1.5rem;
  }

  .story-text {
    font-size: 1rem;
  }

  .dialogue {
    margin-bottom: 10px;
  }

  .character-avatar img {
    width: 50px;
    height: 50px;
  }

  .speech-bubble {
    max-width: 85%;
  }

  .choice-button {
    padding: 12px;
  }

  .progress-bar {
    flex-wrap: wrap;
    justify-content: space-around;
  }

  .progress-connector {
    display: none;
  }

  .progress-step {
    margin: 5px;
  }

  /* Mobile styles for Flamou congratulations */
  .flamou-character {
    flex-direction: column;
    text-align: center;
  }

  .flamou-avatar {
    width: 70px;
    height: 70px;
    margin-right: 0;
    margin-bottom: 10px;
  }

  .flamou-speech-bubble {
    max-width: 280px;
  }

  .flamou-speech-bubble:before {
    left: 50%;
    top: -15px;
    transform: translateX(-50%);
    border-width: 0 15px 15px 15px;
    border-color: transparent transparent #ffd700 transparent;
  }

  .congratulation-message {
    font-size: 1.1rem;
  }
}

/* Tailles de texte différentes pour accessibilité */
.text-small .story-text,
.text-small .speech-bubble p,
.text-small .question-text,
.text-small .choice-button,
.text-small .feedback-message {
  font-size: 0.9rem;
}

.text-large .story-text,
.text-large .speech-bubble p,
.text-large .question-text,
.text-large .choice-button,
.text-large .feedback-message {
  font-size: 1.2rem;
}

/* Styles pour l'animation du badge débloqué */
.badge-unlock-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.8);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  animation: fadeIn 0.5s ease-out;
}

.badge-unlock-animation {
  background-color: #fff;
  border-radius: 20px;
  padding: 30px;
  text-align: center;
  max-width: 400px;
  box-shadow: 0 0 30px rgba(255, 215, 0, 0.6);
  animation: scaleIn 0.5s ease-out;
}

.badge-unlock-animation .badge-icon {
  width: 150px;
  height: 150px;
  margin: 0 auto 20px auto;
  display: block;
  animation: pulse 2s infinite;
}

.badge-unlock-animation h2 {
  color: #ffd700;
  font-size: 2rem;
  margin-bottom: 10px;
}

.badge-unlock-animation h3 {
  color: #333;
  font-size: 1.5rem;
  margin-bottom: 15px;
}

.badge-unlock-animation p {
  color: #666;
  margin-bottom: 20px;
}

.close-animation-btn {
  background-color: #4caf50;
  color: white;
  border: none;
  padding: 12px 24px;
  border-radius: 50px;
  font-weight: bold;
  font-size: 1.1rem;
  cursor: pointer;
  transition: all 0.3s ease;
}

.close-animation-btn:hover {
  background-color: #45a049;
  transform: scale(1.05);
}

@keyframes scaleIn {
  from {
    transform: scale(0.8);
    opacity: 0;
  }
  to {
    transform: scale(1);
    opacity: 1;
  }
}

@keyframes pulse {
  0% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.05);
  }
  100% {
    transform: scale(1);
  }
}

/* Responsive pour l'animation du badge */
@media (max-width: 768px) {
  .badge-unlock-animation {
    padding: 25px;
    max-width: 85%;
    margin: 0 15px;
  }

  .badge-unlock-animation .badge-icon {
    width: 120px;
    height: 120px;
  }

  .badge-unlock-animation h2 {
    font-size: 1.75rem;
  }

  .badge-unlock-animation h3 {
    font-size: 1.3rem;
  }

  .badge-unlock-animation p {
    font-size: 1rem;
  }

  .close-animation-btn {
    padding: 10px 20px;
    font-size: 1rem;
  }
}

@media (max-width: 576px) {
  .badge-unlock-animation {
    padding: 20px 15px;
    max-width: 90%;
    margin: 0 10px;
  }

  .badge-unlock-animation .badge-icon {
    width: 100px;
    height: 100px;
  }

  .badge-unlock-animation h2 {
    font-size: 1.5rem;
  }

  .badge-unlock-animation h3 {
    font-size: 1.2rem;
  }

  .badge-unlock-animation p {
    font-size: 0.95rem;
  }

  .close-animation-btn {
    padding: 10px 18px;
    font-size: 0.95rem;
  }
}

@media (max-width: 400px) {
  .badge-unlock-animation {
    padding: 15px 10px;
    max-width: 95%;
  }

  .badge-unlock-animation .badge-icon {
    width: 80px;
    height: 80px;
  }

  .badge-unlock-animation h2 {
    font-size: 1.3rem;
  }

  .badge-unlock-animation h3 {
    font-size: 1.1rem;
  }

  .badge-unlock-animation p {
    font-size: 0.9rem;
  }

  .close-animation-btn {
    padding: 8px 16px;
    font-size: 0.9rem;
  }
}
</style>
